import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Tag,
  Progress,
  Divider,
  Typography,
  List,
  Card,
  message,
  Tooltip,
  Spin,
} from "antd";
import {
  DownloadOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  CalendarOutlined,
  UserOutlined,
  FileTextOutlined,
  ReloadOutlined,
  LinkOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { exportTabData, getDownloadQuery, getExportHistory } from "../services/generic";
import { getOAuthHeaders } from "../constants/oAuthValidation";

const { Title, Text } = Typography;

const formatDistanceToNow = (date, options = {}) => {
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInMinutes < 1) return options.addSuffix ? "just now" : "0 minutes";
  if (diffInMinutes < 60) return `${diffInMinutes} minutes${options.addSuffix ? " ago" : ""}`;
  if (diffInHours < 24) return `${diffInHours} hours${options.addSuffix ? " ago" : ""}`;
  return `${diffInDays} days${options.addSuffix ? " ago" : ""}`;
};

const mapStatus = (status) => {
  const statusMap = { PE: "pending", IP: "processing", SU: "completed", FA: "failed" };
  return statusMap[status] || status.toLowerCase();
};

const getProgress = (status, triggeredAt, completedAt) => {
  const mappedStatus = mapStatus(status);
  if (mappedStatus === "completed") return 100;
  if (mappedStatus === "failed" || mappedStatus === "pending") return 0;

  if (mappedStatus === "processing" && triggeredAt) {
    const startTime = new Date(triggeredAt).getTime();
    const currentTime = new Date().getTime();
    const elapsed = currentTime - startTime;
    const estimatedDuration = 2 * 60 * 1000; // 2 min
    return Math.min(Math.floor((elapsed / estimatedDuration) * 100), 95);
  }
  return 10;
};

const ExportPopup = ({
  exportType,
  exportLabel,
  reportId,
  windowId,
  tabId,
  totalRecords = 0,
  showExportPopup,
  setShowExportPopup,
  filterData,
  fieldsData,
  inputJson,
  isAnalyticsReport,
  queryData
}) => {
  const [jobs, setJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const genericUrl = process.env.REACT_APP_genericUrl

  const fetchJobs = async () => {
    setIsLoading(true);
    try {
      const params = { type: exportType, reportId, windowId, tabId };
      const data = await getExportHistory(params);
      const result = data;
      // console.log(result);
      if (result) {
        const parsed = typeof result === "string" ? JSON.parse(result) : result;

        const transformed = parsed.map((item) => ({
          id: item.cs_batch_process_queue_id,
          type: exportType.toLowerCase(),
          status: mapStatus(item.status),
          progress: getProgress(item.status, item.triggered_at, item.completed_at),
          fileName: item.download_url?.split("/").pop(),
          createdAt: item.triggered_at,
          completedAt: item.completed_at,
          downloadUrl: item.download_url,
          error: item.error_message,
          createdBy: item.user,
          params: item.params, // DO NOT parse — it's raw SQL
        }));

        setJobs(
          transformed.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        );
      }
      else {
        setJobs([]);
      }
    } catch (error) {
      console.error("Fetch error:", error);
      setJobs([]);
    } finally {
      setIsLoading(false);
    }
  };

  const downloadFile = (url) => {
    setIsLoading(true)
    const fileName = url.split("/").pop();
    axios({ url, method: "GET", responseType: "blob" }).then((res) => {
      const blob = new Blob([res.data]);
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = fileName || "export.xlsx";
      link.click();
    });
    setIsLoading(false)
  };
  const downloadData = async (type) => {
    setIsLoading(true)
    try {
      if (!isAnalyticsReport) {
        const jsonToSend = JSON.stringify(inputJson).replace(/"/g, '\\"')
        // const newToken = JSON.parse(localStorage.getItem("authTokens"));
        const { access_token } = getOAuthHeaders();
        const downloadMutation = {
          query: `query {
        downloadReport(reportId:"${reportId}", reportParam:"${jsonToSend}",exportType:"${type}",isBatch:"Y"){ title, message, downloadURL}
      }`,
        };

        const headers = {
          "Content-Type": "application/json",
          Authorization: `bearer ${access_token}`,
          appId: null
        };

        const response = await axios.post(genericUrl, downloadMutation, { headers: headers }, { async: true }, { crossDomain: true });
        const responseFromServer = response.data.data.downloadReport
        if (responseFromServer.title === "Success") {
          if (responseFromServer.downloadURL === null) {
            message.info(responseFromServer.message)
            fetchJobs();
          } else {
            const fileName = responseFromServer.downloadURL
            downloadFile(fileName)
          }
        } else {
          message.error(responseFromServer.message)
          // setLoading(false)
        }
      } else {
        const jsonToSend = JSON.stringify(inputJson).replace(/"/g, '\\"')
        const { access_token } = getOAuthHeaders()
        const downloadMutation = {
          query: `query {
                    downloadAnalyticsReport(reportId: "${reportId}", queryId: null, reportParam:"${jsonToSend}", exportType:"${type}", isBatch: "Y") {title, message, downloadURL}
                }`,
        }

        const headers = {
          "Content-Type": "application/json",
          Authorization: `bearer ${access_token}`,
          appId: null,
        }
        const response = await axios.post(genericUrl, downloadMutation, { headers: headers })
        const responseFromServer = response.data.data.downloadAnalyticsReport
        if (responseFromServer.title === "Success") {
          if (responseFromServer.downloadURL === null) {
            message.info(responseFromServer.message)
            fetchJobs();
          } else {
            const fileName = responseFromServer.downloadURL
            downloadFile(fileName)
          }
        } else {
          message.error(responseFromServer.message)
        }
      }
    } catch (error) {
      const { message } = JSON.parse(JSON.stringify(error));
      if (message === "Network error: Unexpected token < in JSON at position 0" || message === "Request failed with status code 401") {
        // generateToken();
      } else {
        return Promise.reject(error);
      }
    } finally {
      setIsLoading(false)
    }
  };

  const handleNewExport = async (type) => {
    try {
      if (exportType !== "SQL") {
        message.destroy()
        const exportResponse = await exportTabData({
          windowId,
          ad_tab_id: tabId,
          isDownload: "Y",
          filterData,
          exportType: type,
          fieldsData, // assuming it's a string like: filterDataField: "someValue,"
        });

        if (exportResponse?.downloadURL) {
          console.log("success");
          downloadFile(exportResponse.downloadURL);
        } else {
          message.info(exportResponse?.message || "No download URL received.");
          fetchJobs();
        }
      } else {
        let queryData1 = null;
        if (queryData !== undefined && queryData !== null) {
          const doubleQuoteForDesc = queryData.replace(/\r?\n|\r/g, '\\n')
          queryData1 = doubleQuoteForDesc.replace(/"/g, '\\"')
        }
        const downloadedData = await getDownloadQuery(queryData1);
        if (downloadedData.downloadURL !== null && downloadedData.downloadURL !== undefined) {
          downloadFile(downloadedData.downloadURL)
        }
        else {
          message.info(downloadedData.message)
        }
      }
    } catch (error) {
      console.error(error);
      message.error("Something went wrong during export.");
    }

  };


  const getStatusTag = (status) => {
    switch (status) {
      case "pending": return <Tag color="gold">Pending</Tag>;
      case "processing": return <Tag icon={<SyncOutlined spin />} color="blue">Processing</Tag>;
      case "completed": return <Tag icon={<CheckCircleOutlined />} color="green">Completed</Tag>;
      case "failed": return <Tag icon={<CloseCircleOutlined />} color="red">Failed</Tag>;
      default: return <Tag color="default">Unknown</Tag>;
    }
  };

  useEffect(() => {
    if (showExportPopup) {
      fetchJobs();
      const interval = setInterval(fetchJobs, 3000); // 10,000 ms = 10 seconds
      return () => clearInterval(interval); // Clear on unmount or when popup closes
    }
  }, [showExportPopup]);


  const activeJobs = jobs.filter((j) => ["pending", "processing"].includes(j.status));
  const completedJobs = jobs.filter((j) => j.status === "completed");
  const failedJobs = jobs.filter((j) => j.status === "failed");

  return (
    <Modal
      visible={showExportPopup}
      onCancel={() => setShowExportPopup(false)}
      destroyOnClose
      // closable={false}
      bodyStyle={{ paddingTop: 0 }}
      centered
      // clo
      title={<Title style={{ fontFamily: "Inter", textAlign: "start" }} level={5}><FileExcelOutlined /> Export {exportLabel}</Title>}
      style={{ fontFamily: "Inter" }}
      width="600px"
      footer={[
        <Button key="refresh" icon={<ReloadOutlined />} onClick={fetchJobs} loading={isLoading}>
          Refresh
        </Button>,
        exportType === "AnalyticsReport" ? (
          <>
            <Button key="excel" icon={<FileExcelOutlined />} onClick={() => downloadData("Excel")}>Export Excel</Button>
            <Button key="pdf" icon={<FilePdfOutlined />} onClick={() => downloadData("PDF")}>Export PDF</Button>
          </>
        ) : exportType === "Report" ? (
          <>
            <Button key="new" type="primary" onClick={() => downloadData("Excel")} icon={<FileExcelOutlined />}>Export Excel</Button>
            <Button key="new" type="primary" onClick={() => downloadData("csv")} icon={<FileExcelOutlined />}>Export CSV</Button>
          </>
        ) : (
          <>
            <Button key="new" type="primary" onClick={() => handleNewExport("Excel")} icon={<FileExcelOutlined />}>Export Excel</Button>
            <Button key="new" type="primary" onClick={() => handleNewExport("csv")} icon={<FileExcelOutlined />}>Export CSV</Button>
          </>
        ),
      ]}
    >
      <Spin indicator={<LoadingOutlined className="spinLoader" style={{ marginLeft: "auto", fontSize: "52px" }} spin />} spinning={isLoading}>
        <div style={{ height: "50vh", fontFamily: "Inter", overflow: "auto" }}>
          {activeJobs.length > 0 && (
            <>
              <span style={{ fontWeight: 600 }}>Active Jobs</span>
              <List
                dataSource={activeJobs}
                style={{ maxHeight: "35vh", overflow: "auto" }}
                renderItem={(job) => (
                  <Card bodyStyle={{ padding: 8 }} size="small" style={{ marginBottom: 8 }} title={getStatusTag(job.status)}>
                    <div><Text>{job.type}</Text> - <Text type="secondary">{job.id.slice(-8)}</Text></div>
                    {job.status === "processing" ? (
                      <>
                        <Progress percent={job.progress} size="small" />
                        <Text type="secondary">Processing... {job.progress}%</Text>
                      </>
                    ) : (
                      <Text type="warning"><ExclamationCircleOutlined /> Waiting in queue</Text>
                    )}
                  </Card>
                )}
              />
              <Divider />
            </>
          )}

          {completedJobs.length > 0 && (
            <>
              <span style={{ fontWeight: 600 }}>Ready For Download</span>
              <List
                dataSource={completedJobs}
                style={{ maxHeight: "35vh", overflow: "auto" }}
                renderItem={(job) => (
                  <Card
                    size="small"
                    style={{ marginBottom: 8 }}
                    bodyStyle={{ padding: 8 }}
                    title={getStatusTag(job.status)}
                    extra={
                      <Button
                        size="small"
                        style={{ background: "black", color: "white", borderRadius: "3px", border: "none" }}
                        icon={job.downloadUrl?.startsWith("http") ? <LinkOutlined /> : <DownloadOutlined />}
                        onClick={() => downloadFile(job.downloadUrl)}
                      >
                        {job.downloadUrl?.startsWith("http") ? "Download" : "Download"}
                      </Button>
                    }
                  >
                    <div>
                      <p style={{ marginBottom: "0.1em" }}><CalendarOutlined /> {formatDistanceToNow(new Date(job.completedAt), { addSuffix: true })}</p>
                      <p style={{ marginBottom: "0.1em" }}><UserOutlined /> {job.createdBy}</p>
                      {job.fileName && (
                        <Tooltip title={job.fileName}>
                          <p style={{ marginBottom: "0.1em" }}><FileTextOutlined /> {job.fileName.slice(0, 30)}{job.fileName.length > 30 && "..."}</p>
                        </Tooltip>
                      )}
                    </div>
                  </Card>
                )}
              />
              <Divider style={{ margin: "4px 0" }} />
            </>
          )}

          {failedJobs.length > 0 && (
            <>
              <span style={{ fontWeight: 600 }}>Failed Jobs</span>
              <List
                dataSource={failedJobs}
                style={{ maxHeight: "35vh", overflow: "auto" }}
                renderItem={(job) => (
                  <Card size="small" style={{ marginBottom: 8 }} title={getStatusTag(job.status)}>
                    <Text type="danger">{job.error}</Text>
                    <p style={{ fontSize: 12, color: "#999", marginBottom: "0.1em" }}>Job ID: {job.id.slice(-8)}</p>
                  </Card>
                )}
              />
            </>
          )}

          {jobs.length === 0 && (
            <div style={{ textAlign: "center", margin: "24px 0", color: "#999" }}>
              <FileExcelOutlined style={{ fontSize: 40, color: "#d9d9d9" }} />
              <p>No export jobs found</p>
            </div>
          )}
        </div>
      </Spin>
    </Modal>
  );
};

export default ExportPopup;